/**
 * Storage utilities for client-side caching
 */

// Maximum number of profile IDs to store for each category
const MAX_CACHED_PROFILES = 50;

// Storage keys
const STORAGE_KEYS = {
    VIEWED_FEATURED_PROFILES: 'jw_viewed_featured_profiles',
    LAST_SYNC_TIME: 'jw_last_profile_sync_time',
};

// Sync interval in milliseconds (24 hours)
export const SYNC_INTERVAL = 24 * 60 * 60 * 1000;

interface ViewedProfileRecord {
  profileId: string;
  viewedAt: number; // timestamp
}

// In-memory fallback when localStorage is unavailable
const memoryStorage: Record<string, string> = {};

/**
 * Check if localStorage is available
 */
const isLocalStorageAvailable = (): boolean => {
    try {
        const testKey = '__storage_test__';
        localStorage.setItem(testKey, testKey);
        const result = localStorage.getItem(testKey) === testKey;
        localStorage.removeItem(testKey);
        return result;
    } catch (e) {
        return false;
    }
};

/**
 * Safely get an item from storage (localStorage with fallback to memory)
 */
const safeGetItem = (key: string): string | null => {
    try {
        if (isLocalStorageAvailable()) {
            return localStorage.getItem(key);
        }
        return memoryStorage[key] || null;
    } catch (e) {
        console.warn('Error accessing storage, falling back to memory:', e);
        return memoryStorage[key] || null;
    }
};

/**
 * Safely set an item in storage (localStorage with fallback to memory)
 */
const safeSetItem = (key: string, value: string): void => {
    try {
        if (isLocalStorageAvailable()) {
            localStorage.setItem(key, value);
        } else {
            memoryStorage[key] = value;
        }
    } catch (e) {
        console.warn('Error setting storage item, using memory fallback:', e);
        memoryStorage[key] = value;
    }
};

/**
 * Safely remove an item from storage (localStorage with fallback to memory)
 */
const safeRemoveItem = (key: string): void => {
    try {
        if (isLocalStorageAvailable()) {
            localStorage.removeItem(key);
        } else {
            delete memoryStorage[key];
        }
    } catch (e) {
        console.warn('Error removing storage item, using memory fallback:', e);
        delete memoryStorage[key];
    }
};

/**
 * Retrieves viewed profile IDs from storage
 */
export const getViewedFeaturedProfileIds = (): ViewedProfileRecord[] => {
    try {
        const storedData = safeGetItem(STORAGE_KEYS.VIEWED_FEATURED_PROFILES);
        if (!storedData) return [];

        const parsedData = JSON.parse(storedData) as ViewedProfileRecord[];
        return Array.isArray(parsedData) ? parsedData : [];
    } catch (error) {
        console.error('Error retrieving viewed profile IDs from storage:', error);
        return [];
    }
};

/**
 * Adds a profile ID to the viewed profiles list
 */
export const addViewedProfileId = (profileId: string): void => {
    try {
    // Get current list
        const currentProfiles = getViewedFeaturedProfileIds();

        // Check if profile is already in the list
        const existingIndex = currentProfiles.findIndex(p => p.profileId === profileId);
        if (existingIndex >= 0) {
            // Update the viewedAt time for existing entry
            currentProfiles[existingIndex].viewedAt = Date.now();
        } else {
            // Add new entry
            currentProfiles.push({
                profileId,
                viewedAt: Date.now()
            });
        }

        // Sort by viewedAt (newest first) and limit size
        const updatedProfiles = currentProfiles
            .sort((a, b) => b.viewedAt - a.viewedAt)
            .slice(0, MAX_CACHED_PROFILES);

        // Save back to storage
        safeSetItem(STORAGE_KEYS.VIEWED_FEATURED_PROFILES, JSON.stringify(updatedProfiles));
    } catch (error) {
        console.error('Error adding profile ID to viewed profiles:', error);
    }
};

/**
 * Adds multiple profile IDs to the viewed list
 */
export const addMultipleViewedProfileIds = (profileIds: string[]): void => {
    profileIds.forEach(id => addViewedProfileId(id));
};

/**
 * Checks if we should sync with the server
 */
export const shouldSyncWithServer = (): boolean => {
    try {
        const lastSyncTime = safeGetItem(STORAGE_KEYS.LAST_SYNC_TIME);
        if (!lastSyncTime) return true;

        const lastSync = parseInt(lastSyncTime, 10);
        const currentTime = Date.now();

        // Sync if more than SYNC_INTERVAL has passed
        return (currentTime - lastSync) > SYNC_INTERVAL;
    } catch (error) {
        console.error('Error checking sync status:', error);
        return true; // Default to syncing on error
    }
};

/**
 * Records that a sync has occurred
 */
export const recordSyncTime = (): void => {
    try {
        safeSetItem(STORAGE_KEYS.LAST_SYNC_TIME, Date.now().toString());
    } catch (error) {
        console.error('Error recording sync time:', error);
    }
};

/**
 * Clears the viewed profiles from storage
 */
export const clearViewedProfiles = (): void => {
    try {
        safeRemoveItem(STORAGE_KEYS.VIEWED_FEATURED_PROFILES);
    } catch (error) {
        console.error('Error clearing viewed profiles:', error);
    }
};

/**
 * Gets the IDs of recently viewed profiles (formatted for API)
 */
export const getRecentlyViewedProfileIds = (): string[] => {
    return getViewedFeaturedProfileIds().map(record => record.profileId);
};
